import { useState, useEffect, useCallback } from 'react';
import axios from "axios";

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:5000"; // Backend URL

const useFeatures = () => {
  const [features, setFeatures] = useState([]);
  const [apiConfig, setApiConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeatures = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get('/api/features', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setFeatures(response.data.features || []);
      setApiConfig(response.data.api_config || null);
      setError(null);
    } catch (err) {
      console.error('❌ Error fetching features:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  const hasFeature = useCallback((featureName) => {
    const result = features.includes(featureName) || features.includes('all');

    return result;
  }, [features]);

  const hasTallyIntegration = useCallback(() => {
    const result = hasFeature('tally_integration');

    return result;
  }, [hasFeature]);

  // New: generic Integrations feature flag (supports both 'integration' and 'integrations')
  const hasIntegrationsFeature = useCallback(() => {
    // Temporarily enabled for testing - remove this line when ready for production
    //return true;
    
    // Original logic (uncomment when ready for production):
    const result = hasFeature('integration') || hasFeature('integrations');
    return result;
  }, [hasFeature]);

  const hasCompanyManagement = useCallback(() => {
    const result = hasFeature('company_management') || hasTallyIntegration();

    return result;
  }, [hasFeature, hasTallyIntegration]);

  const getApiHeaders = useCallback(() => {
    if (!apiConfig) return {};
    
    if (apiConfig.auth_method === 'api_key') {
      const apiKey = localStorage.getItem('api_key');
      return apiKey ? { 'X-API-Key': apiKey } : {};
    } else {
      const token = localStorage.getItem('authToken');
      return token ? { 'Authorization': `Bearer ${token}` } : {};
    }
  }, [apiConfig]);

  const getUploadEndpoint = useCallback(() => {
    return apiConfig?.upload_endpoint || '/api/upload_ocr';
  }, [apiConfig]);

  const getStatusEndpoint = useCallback((taskId) => {
    if (apiConfig?.tally_integration) {
      return `/api/tally/status/${taskId}`;
    }
    return `/api/status/${taskId}`;
  }, [apiConfig]);

  return {
    features,
    apiConfig,
    loading,
    error,
    hasFeature,
    hasTallyIntegration,
    hasIntegrationsFeature,
    hasCompanyManagement,
    getApiHeaders,
    getUploadEndpoint,
    getStatusEndpoint,
    refetchFeatures: fetchFeatures
  };
};

export default useFeatures; 